import { APIStatus } from '@application/enums/api-status';
import { createSlice } from '@reduxjs/toolkit';
import { NotificationModule } from '@application/enums/notification-module-enum';
import { ActivityNotificationType, EventNotificationType, TaskNotificationType } from '@application/enums/notification-type-enum';
import { PriorityEnum } from '@application/enums/priority-enum';
import { getAllNotificationPreferencesAsync, getNotificationPreferencesByModuleAsync, getNotificationPreferenceByIdAsync, createNotificationPreferenceAsync, updateNotificationPreferenceAsync, deleteNotificationPreferenceAsync, getModuleCompaniesAsync, updateModuleCompaniesAsync } from './notificationPreferencesThunk';
const initialState = {
    status: APIStatus.IDLE,
    allPreferences: [],
    preferencesByModule: {
        [NotificationModule.Events]: [],
        [NotificationModule.Tasks]: [],
        [NotificationModule.Activities]: []
    },
    selectedPreference: null,
    moduleCompaniesByModule: {
        [NotificationModule.Events]: null,
        [NotificationModule.Tasks]: null,
        [NotificationModule.Activities]: null
    },
    apiError: undefined
};
const moduleFromNumber = (value) => {
    switch (value) {
        case 0:
            return NotificationModule.Events;
        case 1:
            return NotificationModule.Activities;
        case 2:
            return NotificationModule.Tasks;
        default:
            return value;
    }
};
const notificationTypeFromNumber = (value) => {
    switch (value) {
        case 0:
            return EventNotificationType.EventCreated;
        case 1:
            return EventNotificationType.EventDeadline;
        case 2:
            return EventNotificationType.EventRescheduled;
        case 3:
            return ActivityNotificationType.BirthdayCelebration;
        case 4:
            return ActivityNotificationType.WorkAnniversary;
        case 5:
            return ActivityNotificationType.BirthAdoption;
        case 6:
            return ActivityNotificationType.Retirement;
        case 7:
            return ActivityNotificationType.MarriageRegistration;
        case 8:
            return ActivityNotificationType.WeddingAnniversary;
        case 9:
            return ActivityNotificationType.EmployeeOnboarding;
        case 10:
            return ActivityNotificationType.EmployeeDeparture;
        case 11:
            return ActivityNotificationType.EmployeeDeath;
        case 12:
            return ActivityNotificationType.EmployeeIllness;
        case 13:
            return ActivityNotificationType.CompanyAnniversary;
        case 14:
            return TaskNotificationType.TaskAssigned;
        case 15:
            return TaskNotificationType.TaskDeadlineApproaching;
        case 16:
            return TaskNotificationType.TaskCompleted;
        default:
            return value;
    }
};
const priorityFromNumber = (value) => {
    if (value === null || value === undefined)
        return null;
    // Current backend/frontend: High=0, Medium=1, Low=2, None=3
    switch (value) {
        case 0:
            return PriorityEnum.High;
        case 1:
            return PriorityEnum.Medium;
        case 2:
            return PriorityEnum.Low;
        case 3:
            return PriorityEnum.None;
        default:
            return null;
    }
};
const normalizePreference = (pref) => {
    const moduleValue = typeof pref.module === 'number' ? moduleFromNumber(pref.module) : pref.module;
    const notificationTypeValue = typeof pref.notificationType === 'number'
        ? notificationTypeFromNumber(pref.notificationType)
        : pref.notificationType;
    return {
        ...pref,
        module: moduleValue,
        notificationType: notificationTypeValue,
        taskPriority: priorityFromNumber(pref.taskPriority),
        companyIds: pref.companyIds ?? [],
        designations: pref.designations ?? [],
        timingSettings: (pref.timingSettings ?? []).map((ts) => ({
            ...ts,
            priorityType: priorityFromNumber(ts.priorityType)
        }))
    };
};
export const notificationPreferencesSlice = createSlice({
    name: 'notificationPreferences',
    initialState,
    reducers: {
        resetNotificationPreferencesApiState(state) {
            state.status = APIStatus.IDLE;
            state.apiError = undefined;
        },
        clearSelectedPreference(state) {
            state.selectedPreference = null;
        },
        setPreferencesByModule(state, action) {
            const { module, preferences } = action.payload;
            state.preferencesByModule[module] = preferences;
        },
        setModuleCompanies(state, action) {
            state.moduleCompaniesByModule[action.payload.module] = action.payload.companies;
        }
    },
    extraReducers: (builder) => {
        builder
            // Get All
            .addCase(getAllNotificationPreferencesAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(getAllNotificationPreferencesAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            const normalized = action.payload.map(normalizePreference);
            state.allPreferences = normalized;
            // Reset buckets then group by module
            state.preferencesByModule = {
                [NotificationModule.Events]: [],
                [NotificationModule.Tasks]: [],
                [NotificationModule.Activities]: []
            };
            normalized.forEach((pref) => {
                const moduleKey = pref.module;
                if (!state.preferencesByModule[moduleKey]) {
                    state.preferencesByModule[moduleKey] = [];
                }
                state.preferencesByModule[moduleKey].push(pref);
            });
        })
            .addCase(getAllNotificationPreferencesAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            if (action.payload)
                state.apiError = action.payload;
        })
            // Get By Module
            .addCase(getNotificationPreferencesByModuleAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(getNotificationPreferencesByModuleAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            const module = action.meta.arg;
            state.preferencesByModule[module] = action.payload.map(normalizePreference);
        })
            .addCase(getNotificationPreferencesByModuleAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            if (action.payload)
                state.apiError = action.payload;
        })
            // Get By Id
            .addCase(getNotificationPreferenceByIdAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(getNotificationPreferenceByIdAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            state.selectedPreference = action.payload;
        })
            .addCase(getNotificationPreferenceByIdAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            if (action.payload)
                state.apiError = action.payload;
        })
            // Create
            .addCase(createNotificationPreferenceAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(createNotificationPreferenceAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            const preference = normalizePreference(action.payload);
            state.allPreferences.push(preference);
            state.preferencesByModule[preference.module].push(preference);
        })
            .addCase(createNotificationPreferenceAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            if (action.payload)
                state.apiError = action.payload;
        })
            // Update
            .addCase(updateNotificationPreferenceAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(updateNotificationPreferenceAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            const updatedPreference = normalizePreference(action.payload);
            // Update in allPreferences
            const allIndex = state.allPreferences.findIndex((p) => p.id === updatedPreference.id);
            if (allIndex !== -1) {
                state.allPreferences[allIndex] = updatedPreference;
            }
            // Update in preferencesByModule
            const moduleIndex = state.preferencesByModule[updatedPreference.module].findIndex((p) => p.id === updatedPreference.id);
            if (moduleIndex !== -1) {
                state.preferencesByModule[updatedPreference.module][moduleIndex] = updatedPreference;
            }
            // Update selected if it's the same
            if (state.selectedPreference?.id === updatedPreference.id) {
                state.selectedPreference = updatedPreference;
            }
        })
            .addCase(updateNotificationPreferenceAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            if (action.payload)
                state.apiError = action.payload;
        })
            // Delete
            .addCase(deleteNotificationPreferenceAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(deleteNotificationPreferenceAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            const deletedPreference = action.payload;
            // Remove from allPreferences
            state.allPreferences = state.allPreferences.filter((p) => p.id !== deletedPreference.id);
            // Remove from preferencesByModule
            state.preferencesByModule[deletedPreference.module] = state.preferencesByModule[deletedPreference.module].filter((p) => p.id !== deletedPreference.id);
            // Clear selected if it was deleted
            if (state.selectedPreference?.id === deletedPreference.id) {
                state.selectedPreference = null;
            }
        })
            .addCase(deleteNotificationPreferenceAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            if (action.payload)
                state.apiError = action.payload;
        })
            // Get Module Companies
            .addCase(getModuleCompaniesAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(getModuleCompaniesAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            const record = action.payload;
            state.moduleCompaniesByModule[record.module] = record;
        })
            .addCase(getModuleCompaniesAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            if (action.payload)
                state.apiError = action.payload;
        })
            // Update Module Companies
            .addCase(updateModuleCompaniesAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(updateModuleCompaniesAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            const record = action.payload;
            state.moduleCompaniesByModule[record.module] = record;
        })
            .addCase(updateModuleCompaniesAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            if (action.payload)
                state.apiError = action.payload;
        });
    }
});
export const { resetNotificationPreferencesApiState, clearSelectedPreference, setPreferencesByModule, setModuleCompanies } = notificationPreferencesSlice.actions;
export default notificationPreferencesSlice.reducer;
